import { DashboardHeader } from '../DashboardHeader';
import { DashboardControls } from '../DashboardControls';
import { DashboardContent } from './DashboardContent';
import { exportEnergyDataToExcel } from '../../utils/energyExport';
import { ActorTreeTransformer } from '../../transformers/ActorTreeTransformer';
import { useMemo, useCallback } from 'react';
import type { DashboardUIStore } from '../../stores/dashboard-ui-store';
import type { ActorTree } from '../../types/actor-types';
import type { SiteNode } from '../../types/types';
import type { OperatorApp } from '../../../operator-selection/types/operator-types';
import { filterTreeData } from '../../utils/tree-filter-utils';
import { CHARGING_STATUSES } from '../ChargingStatusFilter';
import { useEnergyStore } from '../../stores/energy-store';

interface DashboardLayoutProps {
  uiStore: DashboardUIStore;
  actors: ActorTree[];
  siteData: SiteNode[];
  selectedOperator: OperatorApp | null;
  selectedActorId: string | null;
  onTreeResize: (e: React.MouseEvent) => void;
  onRefreshData: () => Promise<void>;
  onClearError: () => void;
  isLoading?: boolean;

  viewMode?: 'single' | 'operator';

  // Server-side filter callbacks (operator view only) — each triggers an API reload
  operatorStatusFilter?: string[];
  operatorOnlineOnly?: boolean;
  operatorOcppConnected?: boolean;
  onStatusFilterChange?: (statuses: string[] | undefined) => void;
  onUtilizationRangeChange?: (ranges: string[]) => void;
  onOnlineOnlyChange?: (onlineOnly: boolean) => void;
  onOcppConnectedChange?: (connected: boolean | undefined) => void;

  totalCount?: number; // Used for result count badge in operator mode
  onSessionClick?: (sessionId: string | null, machineName?: string, machineId?: number) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export function DashboardLayout({
  uiStore,
  actors,
  siteData,
  selectedOperator,
  selectedActorId,
  onTreeResize,
  onRefreshData,
  onClearError,
  isLoading = false,
  viewMode = 'single',
  operatorStatusFilter,
  operatorOnlineOnly = false,
  operatorOcppConnected,
  onStatusFilterChange,
  onUtilizationRangeChange,
  onOnlineOnlyChange,
  onOcppConnectedChange,
  totalCount,
  onSessionClick,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}: DashboardLayoutProps) {
  
  // Transform actor tree data for the tree view
  const operatorTreeActors = useMemo(() => {
    if (!actors.length || !selectedOperator) return [];

    const operatorId = selectedOperator.operatorId;

    // First try to find the operator directly in the tree
    let rootActor = actors.find(actor => actor.actorId === operatorId);

    // If operator not found, find root actors (whose parent is not in the tree)
    // This handles non-admin users whose operatorId may not match any actor in the tree
    if (!rootActor) {
      const actorIds = new Set(actors.map(a => a.actorId));
      const rootActors = actors.filter(actor => {
        // Root if parent doesn't exist in the tree (includes distributor, deleted actors, etc.)
        return !actor.parentActorId || !actorIds.has(actor.parentActorId);
      });

      // DEFENSIVE: If no roots found but we have actors, return all actors
      // This prevents showing empty tree when data exists
      if (rootActors.length === 0) {
        console.warn('No root actors found, showing all actors as roots');
        // Deduplicate actors by actorId to prevent duplicate rendering
        const uniqueActors = actors.filter((actor, index, self) =>
          index === self.findIndex(a => a.actorId === actor.actorId)
        );
        return uniqueActors;
      }

      // If multiple roots, return all actors (flat tree structure with multiple roots)
      if (rootActors.length > 1) {
        // Deduplicate actors by actorId to prevent duplicate rendering
        const uniqueActors = actors.filter((actor, index, self) =>
          index === self.findIndex(a => a.actorId === actor.actorId)
        );
        return uniqueActors;
      }

      rootActor = rootActors[0];
    }

    const getDescendants = (parentId: number): ActorTree[] => {
      const children = actors.filter(actor => actor.parentActorId === parentId);
      return [...children, ...children.flatMap(child => getDescendants(child.actorId))];
    };

    const result = [rootActor, ...getDescendants(rootActor.actorId)];

    // Deduplicate actors by actorId to prevent duplicate rendering
    const uniqueActors = result.filter((actor, index, self) =>
      index === self.findIndex(a => a.actorId === actor.actorId)
    );

    return uniqueActors;
  }, [actors, selectedOperator]);

  // Transform actor tree data for the table
  const hierarchicalSiteData = useMemo(() => {
    if (!actors.length) return [];
    return ActorTreeTransformer.transformToSiteNodes(actors, uiStore.selectedLocationActorId || undefined);
  }, [actors, uiStore.selectedLocationActorId]);

  // Operator mode: use server-reported count. Single-site: count locally after filter.
  const filteredCount = useMemo(() => {
    if (viewMode === 'operator') return totalCount ?? 0;
    if (!siteData || siteData.length === 0) return 0;

    const filtered = filterTreeData(
      siteData,
      uiStore.selectedChargingStatuses,
      uiStore.selectedAllocatedRanges,
      uiStore.hideInactive
    );

    const countMachines = (nodes: SiteNode[]): number => {
      let count = 0;
      for (const node of nodes) {
        if (node.type === 'machine') count++;
        if (node.children?.length) count += countMachines(node.children);
      }
      return count;
    };

    return countMachines(filtered);
  }, [siteData, uiStore.selectedChargingStatuses, uiStore.selectedAllocatedRanges, uiStore.hideInactive, viewMode, totalCount]);

  // Handle location selection from tree
  const handleLocationSelectFromTree = useCallback((actorId: string) => {
    uiStore.setSkipLocationSelectorApiCall(true);

    // Check if the selected actorId is the root operator (show all locations)
    if (selectedOperator && actorId === selectedOperator.operatorId.toString()) {
      // Root operator selected - show all locations
      uiStore.setSelectedLocation('');
      uiStore.setSelectedLocationActorId('');
      uiStore.setShouldAutoScrollTree(false);
      setTimeout(() => uiStore.setSkipLocationSelectorApiCall(false), 100);
      return;
    }

    const selectedActor = actors.find(actor => actor.actorId.toString() === actorId);

    if (selectedActor && selectedOperator?.operatorId) {
      const findTopLevelParent = (actor: ActorTree): ActorTree | null => {
        if (actor.parentActorId === selectedOperator.operatorId) return actor;
        const parent = actors.find(a => a.actorId === actor.parentActorId);
        return parent ? findTopLevelParent(parent) : null;
      };

      const topLevelParent = findTopLevelParent(selectedActor);

      if (topLevelParent) {
        if (selectedActor.parentActorId === selectedOperator.operatorId) {
          uiStore.setSelectedLocation(actorId);
          uiStore.setSelectedLocationActorId(actorId);
        } else {
          uiStore.setSelectedLocation(topLevelParent.actorId.toString());
          uiStore.setSelectedLocationActorId(actorId);
        }
      } else {
        uiStore.setSelectedLocation(actorId);
        uiStore.setSelectedLocationActorId(actorId);
      }
    } else {
      uiStore.setSelectedLocation(actorId);
      uiStore.setSelectedLocationActorId(actorId);
    }
    
    uiStore.setShouldAutoScrollTree(false);
    setTimeout(() => uiStore.setSkipLocationSelectorApiCall(false), 100);
  }, [actors, selectedOperator, uiStore]);

  // Handle location selection from selector
  const handleLocationSelectorSelect = useCallback(async (actorId: string) => {
    uiStore.setSkipLocationSelectorApiCall(false);
    uiStore.setSelectedLocationActorId(actorId);
    uiStore.setSelectedLocation(actorId);
    uiStore.setShouldAutoScrollTree(true);
  }, [uiStore]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    uiStore.setIsRefreshing(true);
    try {
      await onRefreshData();
    } finally {
      uiStore.setIsRefreshing(false);
    }
  }, [uiStore, onRefreshData]);

  // Handle export — fetches all pages when in operator mode with pagination
  const exportAllData = useEnergyStore(s => s.exportAllData);
  const isExporting = useEnergyStore(s => s.isExporting);
  const handleExport = useCallback(async () => {
    try {
      const allData = await exportAllData();
      exportEnergyDataToExcel(allData);
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, [exportAllData]);

  // Filter callbacks: update local uiStore state (for single-site mode)
  // and trigger server-side reload when in operator mode
  const handleChargingStatusChange = useCallback((statuses: string[]) => {
    uiStore.setSelectedChargingStatuses(statuses);
    if (viewMode === 'operator') {
      // Only send a filter when a specific subset is selected — not all, not none
      const hasActiveFilter = statuses.length > 0 && statuses.length < CHARGING_STATUSES.length;
      const newFilter = hasActiveFilter ? statuses : undefined;
      // Skip if effective filter hasn't changed (e.g. Hide All when already unfiltered)
      const currentFilter = operatorStatusFilter;
      if (newFilter === undefined && currentFilter === undefined) return;
      onStatusFilterChange?.(newFilter);
    }
  }, [viewMode, uiStore, onStatusFilterChange, operatorStatusFilter]);

  const handleAllocatedRangeChange = useCallback((ranges: string[]) => {
    uiStore.setSelectedAllocatedRanges(ranges);
    if (viewMode === 'operator') {
      onUtilizationRangeChange?.(ranges);
    }
  }, [viewMode, uiStore, onUtilizationRangeChange]);

  const handleHideInactiveChange = useCallback((value: boolean) => {
    uiStore.setHideInactive(value);
    if (viewMode === 'operator') {
      onOnlineOnlyChange?.(value);
    }
  }, [viewMode, uiStore, onOnlineOnlyChange]);

  return (
    <div className="p-2 md:p-3 lg:p-6 max-w-full mx-auto overflow-x-hidden">
      <DashboardHeader
        selectedLocation={uiStore.selectedLocation}
        onLocationChange={uiStore.setSelectedLocation}
        onLocationSelect={handleLocationSelectorSelect}
        onToggleTreeView={uiStore.toggleTreeView}
        isTreeViewVisible={uiStore.isTreeViewVisible}
        selectedActorId={selectedActorId || undefined}
        skipLocationSelectorApiCall={uiStore.skipLocationSelectorApiCall}
        hasActiveFilters={uiStore.selectedChargingStatuses.length > 0 || uiStore.selectedAllocatedRanges.length > 0}
        // NEW: Filter props
        operatorStatusFilter={operatorStatusFilter}
        operatorOnlineOnly={operatorOnlineOnly}
        onStatusFilterChange={onStatusFilterChange}
        onOnlineOnlyChange={onOnlineOnlyChange}
        viewMode={viewMode}
      />
      
      <DashboardControls
        activeTab={uiStore.activeTab}
        onTabChange={uiStore.setActiveTab}
        onRefresh={handleRefresh}
        isRefreshing={uiStore.isRefreshing}
        data={siteData || []}
        onExport={handleExport}
        columnVisibility={uiStore.columnVisibility}
        onColumnVisibilityChange={uiStore.setColumnVisibility}
        selectedChargingStatuses={uiStore.selectedChargingStatuses}
        onSelectedChargingStatusesChange={handleChargingStatusChange}
        selectedAllocatedRanges={uiStore.selectedAllocatedRanges}
        onSelectedAllocatedRangesChange={handleAllocatedRangeChange}
        excludeInactive={uiStore.hideInactive}
        onExcludeInactiveChange={handleHideInactiveChange}
        ocppConnected={operatorOcppConnected}
        onOcppConnectedChange={onOcppConnectedChange}
        viewMode={viewMode}
        filteredCount={filteredCount}
        isLoading={isLoading}
        unitMode={uiStore.unitMode}
        onUnitModeChange={uiStore.setUnitMode}
        isExporting={isExporting}
      />

      <DashboardContent
        isTreeViewVisible={uiStore.isTreeViewVisible}
        isResizing={uiStore.isResizing}
        treeViewWidth={uiStore.treeViewWidth}
        operatorTreeActors={operatorTreeActors}
        selectedLocationActorId={uiStore.selectedLocationActorId}
        shouldAutoScrollTree={uiStore.shouldAutoScrollTree}
        onLocationSelect={handleLocationSelectFromTree}
        onTreeResize={onTreeResize}
        siteManagementData={siteData}
        hierarchicalSiteData={hierarchicalSiteData}
        columnVisibility={uiStore.columnVisibility}
        isLoading={isLoading}
        selectedChargingStatuses={uiStore.selectedChargingStatuses}
        selectedAllocatedRanges={uiStore.selectedAllocatedRanges}
        hideInactive={uiStore.hideInactive}
        viewMode={viewMode}
        onSessionClick={onSessionClick}
        unitMode={uiStore.unitMode}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
      />
    </div>
  );
}